import { z } from 'zod';

/**
 * What an audit row records. `<entity>.<verb>`, mirroring the permission-key
 * grammar so a reader can line an action up with the permission that allowed it.
 *
 * P2 declares the **access-control** actions, because N22 requires every access
 * mutation to be audited and access control is the first thing built. Later
 * phases append their own (verification decisions, ID-media views, integration
 * writes, enforcement actions); the audit interceptor and the admin log viewer
 * arrive with P15/P10.
 */
export const AUDIT_ACTIONS = [
  'access.role.created',
  'access.role.updated',
  'access.role.deleted',
  'access.user_roles.assigned',
  'access.user_permission.set',
  'access.user_permission.cleared',
  // P10.6g — one reviewed save of a whole access panel. Its own action rather
  // than a burst of the three above: the operator performed ONE change, judged
  // once, and the trail's job is to answer "what was this person's authority
  // before, and after" — which several rows of one key each cannot.
  'access.user_access.reviewed_save',
  'access.sessions.revoked',
  // P10.6h — ONE session ended, beside the force-logout above. Two actions
  // rather than one with a count, because the two answer different questions:
  // "who logged this account out of everything" is an authority event, while
  // "who ended the session on that device" names a single device and must stay
  // findable as such. Both rows point at the ACCOUNT (`access.users`), so an
  // account's trail is complete; a session id alone means nothing to a reader,
  // and it is in the snapshot for the one who needs it.
  'access.session.revoked',
  'user.status.changed',
  'settings.value.set',
  'settings.value.cleared',
  // P4 — the integrations vault (N21). The snapshots on these rows are the
  // MASKED status, never a credential.
  'integrations.config.updated',
  'integrations.provider.activated',
  // P5 — every staff read of verification evidence (`id_document`, `selfie`) is
  // recorded before the read url is minted (N12). The snapshot names the asset
  // and its kind; there is no way to record the bytes and no reason to.
  'media.asset.viewed',
  // P6 — the question engine. Every builder write is audited, because a
  // question's configuration decides what members are asked, what is required of
  // them, and — through the visibility policy — who can see the answer. A
  // visibility change is recorded separately from an ordinary edit: it is the
  // one field of a question whose change alters what is already exposed about
  // members who answered long ago.
  'questions.section.created',
  'questions.section.updated',
  'questions.section.deleted',
  'questions.section.reordered',
  'questions.question.created',
  'questions.question.updated',
  'questions.question.deleted',
  'questions.question.reordered',
  // A **move** is one question changing place — possibly into another section —
  // while `reordered` is a whole section being rearranged at once. Two actions
  // rather than one because their snapshots answer different questions: a
  // reorder's records the order that was applied, a move's records where this
  // question came from and where it went, which is the only form in which
  // "who moved this out of the section it was authored in" is answerable.
  'questions.question.moved',
  // A **set** of questions switched on or off in one write (P10.6b). A single
  // question's status change still rides on `updated`, because it arrives
  // through the ordinary edit form alongside everything else; this row exists
  // because a bulk write has no single entity to point at, and one recorded as
  // `updated` with no `entityId` would be indistinguishable from a corrupt
  // single-edit row. The ids it covered are in the snapshot.
  'questions.question.status_changed',
  'questions.question.visibility_changed',
  'questions.option.created',
  'questions.option.updated',
  'questions.option.deleted',
  'questions.option.reordered',
  // P7 — legal documents and consent (N16). `version.published` is the row that
  // matters most in this family and the one that can never be undone: it is the
  // moment a text became law and, at `material`, the moment every member on the
  // platform was stopped and asked to accept again. The snapshot records the
  // materiality and the effective date — the two facts the decision consisted
  // of. Consent records themselves are NOT audited: `legal.user_consents` is
  // already an append-only trail, and a second copy of it would only be a second
  // thing to keep in step.
  'legal.document.created',
  'legal.document.updated',
  'legal.document.deleted',
  'legal.document.reordered',
  'legal.version.created',
  'legal.version.updated',
  'legal.version.deleted',
  'legal.version.published',
  // P8 — the AI platform (N15). Only the *destructive* cache operations are
  // audited: an AI call is recorded in `ai.ai_usage_logs`, which is a complete
  // append-only trail of every call the platform made or refused, so auditing
  // calls again would be a second copy of it. Evicting cached answers is a
  // different act — it is the one staff action that makes the platform pay a
  // vendor again, and after a prompt or model change it is how a poisoned
  // corpus is cleared. The snapshot records the scope and the count, never a
  // cached value.
  'ai.cache.purged',
  'ai.cache.entry.deleted',
  // P19a — a reviewer's verdict on a member report. The **one** moderation act
  // that is audited, and the omissions matter as much as the entry: a member
  // blocking or reporting somebody is not staff activity and belongs in no
  // staff trail, and the enforcement a reviewer may take afterwards already
  // writes `user.status.changed` through `UserStatusService` (N9). What this row
  // answers is the question neither of those can — who looked at this complaint,
  // when, and what they concluded — which is what an appeal, and P20's
  // overturn path, will be read against. The snapshot carries the verdict and
  // the reviewer's note; it never carries the reporter's words, which are
  // already on the report row and are a member's, not a decision's.
  'moderation.report.resolved',
  // P22a — the curriculum, and the two staff acts on a member's record.
  //
  // The authoring half is audited for the reason the question builder is: what a
  // course says and what its test marks correct decide whether a member earns
  // **money off**, and "who changed the passing score last Tuesday?" is a
  // question a disputed grant will eventually ask.
  'courses.course.created',
  'courses.course.updated',
  'courses.course.deleted',
  'courses.course.reordered',
  'courses.lesson.created',
  'courses.lesson.updated',
  'courses.lesson.deleted',
  'courses.lesson.reordered',
  'courses.test.created',
  'courses.test.updated',
  'courses.test.deleted',
  'courses.question.created',
  'courses.question.updated',
  'courses.question.deleted',
  'courses.question.reordered',
  'courses.discount_rule.created',
  'courses.discount_rule.updated',
  'courses.discount_rule.deleted',
  // The two that act on a **member's** record rather than on the catalogue, and
  // the two `03-courses-discounts.md` names as audited in as many words. A reset
  // hands back attempts the member had spent; a revoke takes away value they had
  // earned. Both are staff overriding an outcome the platform produced, which is
  // exactly the shape a trail exists for.
  'courses.attempts.reset',
  'courses.grant.revoked',

  // Billing (P23a1).
  //
  // The catalogue is audited because a product's `duration_days`, its tier and
  // its store ids decide what a member gets for their money, and "who shortened
  // the annual plan?" is a question a refund request will eventually ask.
  'billing.product.created',
  'billing.product.updated',
  'billing.product.deleted',
  // The two that act on a **member's** commercial access rather than on the
  // catalogue, and the only two writes in the platform that hand out or take
  // away paid features with no store and no receipt behind them. A promo grant
  // gives away commercial value; a revoke removes access somebody may have paid
  // for. Both are irreversible, and neither has any other paper trail.
  'billing.entitlement.granted',
  'billing.entitlement.revoked',
  // IAP offer codes (P23a2). Audited because a wrong code is invisible from
  // this side: the store accepts it, discounts the wrong amount or nothing at
  // all, and the first report is a member saying the price was not what the app
  // promised. The trail carries the code itself, so "who changed it, and to
  // what?" has an answer. The webhook's own writes are NOT here — a store did
  // those, and `billing_events` is their trail.
  'billing.offer_code.created',
  'billing.offer_code.updated',
  'billing.offer_code.deleted',
  // Direct payment orders (P23a3) — the staff-managed channel, and the only
  // place a human both takes money and hands out access. `EntitlementGrantsService.grant`
  // writes NO audit row of its own (the store channel's trail is `billing_events`),
  // so for a direct order these ARE the trail: without them a confirmed
  // subscription has no record of who confirmed it.
  //
  // Four actions rather than one `updated`, because the four are different acts
  // with different consequences and a reader filtering for "who refunded
  // things" must not have to parse a payload to find out.
  'billing.direct_order.created',
  'billing.direct_order.updated',
  'billing.direct_order.submitted',
  'billing.direct_order.confirmed',
  'billing.direct_order.rejected',
  'billing.direct_order.refunded',
  'billing.direct_order.discarded',
  // P23b4 — the receipt. Two acts rather than one `evidence_changed`, for the
  // reason the seven above are separate: attaching completes the file, while
  // detaching schedules the bytes for destruction, and a reader asking "who
  // dropped the proof of this payment?" must not have to read a payload to find
  // it. Neither of these is the *view* — a staff read of the receipt is
  // `media.asset.viewed`, written by the mint (N12), because the read is
  // authorized on the asset's kind and belongs to whichever surface asks.
  'billing.direct_order.evidence_attached',
  'billing.direct_order.evidence_detached',
  // F3 — the member's own door. One action rather than `created` + `submitted`
  // because the two are one act there, and a reader asking "which orders did
  // members file themselves" must not have to join on the actor's role.
  'billing.direct_order.member_submitted',
  // P23b3a — every staff read of a stored RevenueCat payload, recorded BEFORE
  // the payload is returned. `media.asset.viewed` is the precedent and the
  // reasoning is identical: the column is `hidden` (N2) and is kept because a
  // dispute is answered by what the store actually said, so it has to be
  // readable by somebody — and a read of a third party's raw record of a member
  // is a read worth naming. The snapshot names the ROW (`externalEventId`,
  // `eventType`, `appUserId`); the payload itself is never copied into the
  // trail, for `media.asset.viewed`'s reason — there is no way to record the
  // bytes and no reason to. The list is not audited: it carries no payload.
  'billing.event.payload_viewed',
  // A purchase the store held and the ledger lacked, applied because the MEMBER
  // asked (`POST /me/billing/sync`). The member is the actor: no staff and no
  // webhook did this, and `billing_events` never saw it.
  'billing.purchase.reconciled',
  // A store sold a plan whose audience is the other gender's — through the store's own
  // subscription settings, which list every product in a group. Granted (the money moved), flagged.
  'billing.purchase.audience_mismatch',
  // P24a — the wali relationship. Audited in full because its done-when says so,
  // and because of what the rows are for: a guardian can read a woman's private
  // conversations, so "who could see this, and between which two instants" has
  // to be answerable from the trail alone — long after the link row itself has
  // been ended and stops saying anything about the window it covered.
  //
  // Issuing is recorded because every outstanding code is a key; the code
  // itself is NEVER in a snapshot, for `media.asset.viewed`'s reason turned
  // around — there is a way to record it and it must not be taken.
  'guardian.invite.issued',
  'guardian.invite.revoked',
  // The one row that names both people. Its `after` is what a later question
  // about access starts from.
  'guardian.link.created',
  // Ending is THREE actions rather than one with a reason field, because who
  // ended it is the first thing a reader asks and a payload is the wrong place
  // to answer it: she removed him, he stepped down, or an account closure took
  // it — and only the first is somebody exercising the control this phase built.
  'guardian.link.revoked_by_ward',
  'guardian.link.left_by_guardian',
  'guardian.link.ended_by_account_closure',
  // P24b1 — the reads the whole relationship exists to permit, and the first
  // guardian acts that disclose somebody's words rather than record a state
  // change.
  //
  // `media.asset.viewed`'s shape exactly: awaited, written BEFORE the payload is
  // returned, so a read that was served but not recorded cannot happen. The
  // snapshot names the ROW — ward, conversation, and the cap the deployment
  // placed on the disclosure — and never a message body or a profile answer,
  // because an audit table is long-lived and widely read, and copying the
  // contents here would double the disclosure the entry exists to police.
  //
  // TWO actions rather than one, for the reason the three ending actions above
  // are three: "did he glance at the list, or did he read what she wrote" is the
  // first thing a reader of this trail asks, and a payload is the wrong place to
  // answer it. And unlike `billing.event.payload_viewed` — whose list "is not
  // audited: it carries no payload" — this list DOES carry payload: every
  // counterparty the wali was shown. Leaving it out would let the trail answer
  // which thread he opened while missing how much of her life he saw.
  'guardian.conversations.listed',
  'guardian.conversation.viewed',
  // P24b2 — the gate. The first guardian action that CHANGES what his ward may
  // do, rather than disclosing something to him.
  //
  // Written AFTER the claim lands, which is the opposite of the two reads above
  // and deliberately so: there the disclosure IS the act, so a row that is not
  // written must stop it; here the act is a state change that the claim may
  // legitimately refuse, and auditing first would record an approval that never
  // happened. `guardian.link.created` sets the same order.
  'guardian.match.approved',
  // The platform opening a conversation nobody is left to approve, when a ward's
  // last link ends. Its own action rather than a variant of the one above,
  // because `actorUserId` is NULL and "who opened this" is the first question
  // asked of either row — the same argument that made ending a link three
  // actions instead of one with a reason field.
  'guardian.match.released',
  // P25a1 — the partner venue directory.
  //
  // A venue is where two matched members are told to meet **in person**, so the
  // address and the contact on it are the platform's answer to "where did you
  // send them, and who was supervising?". That question does not get asked while
  // everything goes well, which is exactly why the trail has to exist before it
  // is needed. Publication rides on `updated` rather than earning its own action:
  // the switch writes one column and the snapshot already shows which.
  'meetings.venue.created',
  'meetings.venue.updated',
  'meetings.venue.deleted',
  // A venue's photographs, separately from its copy: the pictures are what a
  // member decides on, and "who put this image in front of matched pairs, and
  // who took it away" is a different question from who edited the address. The
  // reorder earns its own action for the same reason publication did not — it
  // changes no column the snapshot shows, only the arrangement.
  'meetings.venue.photo.attached',
  'meetings.venue.photo.reordered',
  'meetings.venue.photo.detached',
  // P25b1 — the windows a venue offers.
  //
  // A slot is what a booking will point at, so "when was this evening opened,
  // and who closed it" is the question behind every meeting that did not happen.
  // Closing rides on `updated` for the reason publication does on a venue: the
  // switch writes one column and the snapshot shows which. The DELETE earns its
  // own action because it is the one act here that can strand somebody — it is
  // refused once a booking names the row (P25c), and the entry is the record of
  // the last moment it was still allowed.
  'meetings.venue.slot.created',
  'meetings.venue.slot.updated',
  'meetings.venue.slot.deleted',
  // The recurring generator, separately from `created`: it is one operator act
  // producing tens of rows, and forty individual `created` entries would bury
  // the fact that they were one decision — while a snapshot per row would say
  // nothing about the twenty the batch passed over.
  'meetings.venue.slot.batch.created',
  // Bookings (P25c). Only the STAFF acts are audited: a member requesting,
  // confirming or cancelling their own meeting is ordinary product use, and
  // auditing it would fill the trail with rows nobody investigates — the same
  // line `modules/chat` draws by not auditing a message.
  //
  // Recording an outcome is different on both counts. It is a staff judgement
  // about whether two people turned up, it is the fact a partner institution is
  // measured on, and it is the one a member may later dispute — so the trail
  // keeps who said it and when.
  'meetings.booking.outcome.recorded',
  // A STAFF cancel (P25d), and only a staff one. The asymmetry is the same
  // judgement as above, applied to the three ways a booking gets called off: a
  // member cancelling their own meeting is ordinary product use, and the
  // platform cancelling because the match ended (`cancelForMatch`) has no actor
  // at all — the trail's first question is "who", and there is nobody to name.
  // Staff cancelling is the only one with both an actor and two members who were
  // told the meeting was happening and may ask why it is not.
  'meetings.booking.cancelled',
  // The demo seed stamping the run it just made (`apps/api/src/db/seeds/demo`):
  // a development database that looks populated should say by whom and when,
  // so a walkthrough's data is never mistaken for real accounts.
  'seed.demo.applied',
  // D9a — the windows staff publish for management interviews, mirroring the
  // venue slot block above for the same reasons: what was published decides
  // which appointments a waiting member can be offered, and the delete is the
  // one act that can strand somebody (refused once an interview names the row).
  'verification.interview.slot.created',
  'verification.interview.slot.updated',
  'verification.interview.slot.deleted',
  'verification.interview.slot.batch.created',
  // P27c1 — a staff read of a member's profile ANSWERS, the N18 exception on
  // `members.read`. `media.asset.viewed`'s shape exactly: awaited, written
  // BEFORE the payload is returned. The snapshot names the member and the
  // moment; the answers themselves are never copied into the trail, for the
  // same reason the bytes of an id document are not.
  'profile.answers.viewed',
] as const;

export const AuditActionSchema = z.enum(AUDIT_ACTIONS);

export type AuditAction = (typeof AUDIT_ACTIONS)[number];
